import React from 'react';

import { GraphData } from './common';
import {
	statisticsFlex as statisticsFlexStyle,
	statisticsGrid as statisticsGridStyle,
	statisticsTile as statisticsTileStyle,
	statisticsTileValue as statisticsTileValueStyle,
} from './styles.module.css';

interface StatisticsBlockProps {
	displayTitle: string;
	subtitle: string;
	dataItems?: GraphData;
}

interface StatisticsTileProps {
	prefix?: string;
	value: number;
	suffix?: string;
	label?: string;
}

const StatisticsTile = ({ prefix, value, suffix, label }: StatisticsTileProps): JSX.Element => {
	// Format numbers over 9999 with suffix
	let formattedValue = `${value}`;
	if (value > 9999) {
		formattedValue = `${new Intl.NumberFormat('en', {
			notation: 'compact',
			compactDisplay: 'short',
		}).format(value)}`.toLowerCase();
	}
	return (
		<div className={statisticsTileStyle}>
			<div className={statisticsTileValueStyle}>
				<span>{prefix}</span>
				<h1>{formattedValue}</h1>
				<span>{suffix}</span>
			</div>
			<p>{label}</p>
		</div>
	);
};

export const StatisticsBlock = ({ dataItems }: StatisticsBlockProps) => {
	if (!dataItems) return <></>;

	const labels = dataItems.xValues ?? [];
	const data = dataItems.yValues ?? [];
	const prefix = dataItems.label ? (dataItems.label === '%' ? '' : dataItems.label) : '';
	const suffix = dataItems.label ? (dataItems.label === '%' ? '%' : '') : '';
	if (data.length === 0) {
		return <></>;
	}
	// We are only interested in at least one and at most 4 items, so if there are more,
	// just dump them
	const maxItems = 4;
	const truncatedLabels = labels.slice(0, maxItems);
	const truncatedData = data.slice(0, maxItems);

	const items = truncatedData.map((dataItem, index) => {
		const label = truncatedLabels[index];
		return { prefix, value: dataItem, suffix, label };
	});

	if (data.length < 4) {
		return (
			<div className={statisticsFlexStyle}>
				{items.map(({ prefix, value, suffix, label }) => (
					<StatisticsTile prefix={prefix} value={value} suffix={suffix} label={label} />
				))}
			</div>
		);
	} else {
		return (
			<div className={statisticsGridStyle}>
				{items.map(({ prefix, value, suffix, label }) => (
					<StatisticsTile prefix={prefix} value={value} suffix={suffix} label={label} />
				))}
			</div>
		);
	}
};
